export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly note?: string;

  constructor(
    status: number,
    code: string,
    message?: string,
    note?: string,
  ) {
    super(message ?? code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.note = note;
  }
}

export interface Me {
  userId: string;
  email: string;
  createdAt: string;
  canManageUsers: boolean;
}

export interface DashUser {
  id: string;
  email: string;
  createdAt: string;
  canManageUsers: boolean;
}

/** Operator-managed player UID group for targeted publish. */
export interface UserGroup {
  id: string;
  name: string;
  uids: number[];
  uidCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface MagicStartResult {
  ok: true;
  devEchoLink?: string;
}

export interface CaseVersionSummary {
  id: string;
  locale: string;
  versionN: number;
  status: string;
}

export type CaseAudienceKind = "full" | "targeted" | "mixed";

export interface CaseAudience {
  kind: CaseAudienceKind;
  groupIds: string[];
  publishGroups: Array<{ id: string; name: string }>;
}

export type CaseLocalePublishKind = "unpublished" | "full" | "targeted";

export interface CaseLocalePublish {
  locale: string;
  published: boolean;
  kind: CaseLocalePublishKind;
  groupIds: string[];
  publishGroups: Array<{ id: string; name: string }>;
}

export interface CaseSummary {
  id: string;
  timezone: string;
  sortOrder: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  locales: string[];
  latestVersion: CaseVersionSummary | null;
  title: string | null;
  /** Present when status is published. */
  audience?: CaseAudience | null;
  /** Per-locale live publish state. Present on the case list. */
  localePublishes?: CaseLocalePublish[];
}

export interface CaseVersion {
  id: string;
  caseId: string;
  locale: string;
  versionN: number;
  status: string;
  phoneJsonR2Key: string | null;
  cleanedScriptR2Key: string | null;
  meta: Record<string, unknown> | null;
  parentVersionId: string | null;
  phoneRuntimeVersion: string;
  branchLabel: string | null;
  createdAt: string;
  updatedAt: string;
  /** Pipeline step keys that have succeeded on this version. */
  succeededSteps?: string[];
  /** True when this version has at least one pipeline run. */
  hasPipeline?: boolean;
}

export type StartFromStep =
  | "generate_phone"
  | "refine_phone"
  | "experience_guide"
  | "asset_plan"
  | "head_refs"
  | "gen_assets"
  | "review"
  | "enrich";

export interface CaseUpload {
  id: string;
  caseId: string;
  caseVersionId: string | null;
  kind: string;
  filename: string | null;
  r2Key: string;
  publicUrl: string | null;
  extractTextR2Key: string | null;
  createdBy: string;
  createdAt: string;
}

export interface CaseDetail {
  case: CaseSummary;
  locales: Array<{
    caseId: string;
    locale: string;
    publishedVersionId: string | null;
  }>;
  versions: CaseVersion[];
  uploads: CaseUpload[];
}

export interface CreateCaseResult {
  case: CaseSummary;
  version: CaseVersion;
  uploads: CaseUpload[];
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
  fileCount: number;
  completedFiles: number;
}

export type UploadProgressHandler = (progress: UploadProgress) => void;

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as {
      error?: string;
      note?: string;
      detail?: string;
    };
    const note =
      typeof body.note === "string"
        ? body.note
        : typeof body.detail === "string"
          ? body.detail
          : undefined;
    return new ApiError(
      res.status,
      body.error ?? "request_failed",
      undefined,
      note,
    );
  } catch {
    return new ApiError(res.status, "request_failed");
  }
}

function parseXhrError(xhr: XMLHttpRequest): ApiError {
  try {
    const body = JSON.parse(xhr.responseText) as {
      error?: string;
      note?: string;
    };
    return new ApiError(
      xhr.status,
      body.error ?? "request_failed",
      undefined,
      typeof body.note === "string" ? body.note : undefined,
    );
  } catch {
    return new ApiError(xhr.status || 0, "request_failed");
  }
}

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  const isForm =
    typeof FormData !== "undefined" && init?.body instanceof FormData;
  if (init?.body && !isForm && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Real upload progress via XHR (fetch has no upload progress events). */
function postFormWithProgress<T>(
  path: string,
  form: FormData,
  onProgress?: (loaded: number, total: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded, event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (xhr.status === 204 || !xhr.responseText) {
          resolve(undefined as T);
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(new ApiError(xhr.status, "invalid_response"));
        }
        return;
      }
      reject(parseXhrError(xhr));
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error"));
    xhr.onabort = () => reject(new ApiError(0, "aborted"));
    xhr.send(form);
  });
}

function emptyProgress(fileCount: number): UploadProgress {
  return {
    loaded: 0,
    total: 0,
    percent: 100,
    fileCount,
    completedFiles: fileCount,
  };
}

/** Upload every file in parallel (no concurrency cap). Aggregates byte progress. */
async function uploadFilesConcurrent(
  caseId: string,
  files: File[],
  onProgress?: UploadProgressHandler,
): Promise<CaseUpload[]> {
  if (files.length === 0) {
    onProgress?.(emptyProgress(0));
    return [];
  }

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const loadedBytes = new Array<number>(files.length).fill(0);
  let completedFiles = 0;

  const emit = () => {
    const loaded = loadedBytes.reduce((sum, n) => sum + n, 0);
    const total = totalBytes;
    const percent =
      total === 0 ? 100 : Math.min(100, Math.round((loaded / total) * 1000) / 10);
    onProgress?.({
      loaded,
      total,
      percent,
      fileCount: files.length,
      completedFiles,
    });
  };

  emit();

  const path = `/api/cases/${encodeURIComponent(caseId)}/uploads`;
  const results = await Promise.all(
    files.map(async (file, index) => {
      const form = new FormData();
      form.append("files", file);
      const result = await postFormWithProgress<{ uploads: CaseUpload[] }>(
        path,
        form,
        (loaded, total) => {
          if (total > 0) {
            loadedBytes[index] = Math.min(file.size, (loaded / total) * file.size);
          } else {
            loadedBytes[index] = Math.min(file.size, loaded);
          }
          emit();
        },
      );
      loadedBytes[index] = file.size;
      completedFiles += 1;
      emit();
      return result.uploads;
    }),
  );

  return results.flat();
}

export type BuildInfo = {
  id: string;
  tag: string | null;
  timestamp: string | null;
};

export const api = {
  /** Current Worker version (public; used for deploy-update detection). */
  build(): Promise<BuildInfo> {
    return request<BuildInfo>("/api/build");
  },

  me(): Promise<Me> {
    return request<Me>("/api/auth/me");
  },

  magicStart(email: string, returnTo?: string): Promise<MagicStartResult> {
    return request<MagicStartResult>("/api/auth/magic/start", {
      method: "POST",
      body: JSON.stringify({ email, returnTo }),
    });
  },

  logout(): Promise<{ ok: true }> {
    return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
  },

  listUsers(): Promise<{ users: DashUser[]; canManageUsers: boolean }> {
    return request<{ users: DashUser[]; canManageUsers: boolean }>(
      "/api/users",
    );
  },

  createUser(email: string): Promise<{ user: DashUser }> {
    return request<{ user: DashUser }>("/api/users", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  deleteUser(userId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/users/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  },

  listUserGroups(): Promise<{ groups: UserGroup[] }> {
    return request<{ groups: UserGroup[] }>("/api/user-groups");
  },

  getUserGroup(groupId: string): Promise<{ group: UserGroup }> {
    return request<{ group: UserGroup }>(
      `/api/user-groups/${encodeURIComponent(groupId)}`,
    );
  },

  createUserGroup(input: {
    name: string;
    uids?: number[];
  }): Promise<{ group: UserGroup }> {
    return request<{ group: UserGroup }>("/api/user-groups", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  renameUserGroup(
    groupId: string,
    name: string,
  ): Promise<{ group: UserGroup }> {
    return request<{ group: UserGroup }>(
      `/api/user-groups/${encodeURIComponent(groupId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ name }),
      },
    );
  },

  deleteUserGroup(groupId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/user-groups/${encodeURIComponent(groupId)}`,
      { method: "DELETE" },
    );
  },

  replaceUserGroupUids(
    groupId: string,
    uids: number[],
  ): Promise<{ group: UserGroup }> {
    return request<{ group: UserGroup }>(
      `/api/user-groups/${encodeURIComponent(groupId)}/uids`,
      {
        method: "PUT",
        body: JSON.stringify({ uids }),
      },
    );
  },

  addUserGroupUids(
    groupId: string,
    uids: number[],
  ): Promise<{ group: UserGroup }> {
    return request<{ group: UserGroup }>(
      `/api/user-groups/${encodeURIComponent(groupId)}/uids`,
      {
        method: "POST",
        body: JSON.stringify({ uids }),
      },
    );
  },

  removeUserGroupUids(
    groupId: string,
    uids: number[],
  ): Promise<{ group: UserGroup }> {
    return request<{ group: UserGroup }>(
      `/api/user-groups/${encodeURIComponent(groupId)}/uids`,
      {
        method: "DELETE",
        body: JSON.stringify({ uids }),
      },
    );
  },

  listCases(): Promise<{ cases: CaseSummary[] }> {
    return request<{ cases: CaseSummary[] }>("/api/cases");
  },

  getCase(caseId: string): Promise<CaseDetail> {
    return request<CaseDetail>(`/api/cases/${encodeURIComponent(caseId)}`);
  },

  patchCase(
    caseId: string,
    input: {
      timezone?: string;
      sortOrder?: number;
    },
  ): Promise<{ case: CaseSummary }> {
    return request<{ case: CaseSummary }>(
      `/api/cases/${encodeURIComponent(caseId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  },

  deleteCase(caseId: string): Promise<DeleteCaseResult> {
    return request<DeleteCaseResult>(
      `/api/cases/${encodeURIComponent(caseId)}`,
      { method: "DELETE" },
    );
  },

  async createCase(input: { text: string }): Promise<CreateCaseResult> {
    const text = input.text?.trim() ?? "";
    if (!text) {
      throw new ApiError(400, "script_required");
    }
    return request<CreateCaseResult>("/api/cases", {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },

  async addUploads(
    caseId: string,
    input: { text?: string; files?: File[] },
    onUploadProgress?: UploadProgressHandler,
  ): Promise<{ uploads: CaseUpload[] }> {
    const files = input.files ?? [];
    const hasText = Boolean(input.text?.trim());
    if (!hasText && files.length === 0) {
      throw new ApiError(400, "no_upload");
    }

    const jobs: Array<Promise<CaseUpload[]>> = [];

    if (hasText) {
      jobs.push(
        request<{ uploads: CaseUpload[] }>(
          `/api/cases/${encodeURIComponent(caseId)}/uploads`,
          {
            method: "POST",
            body: JSON.stringify({ text: input.text }),
          },
        ).then((r) => r.uploads),
      );
    }

    if (files.length > 0) {
      jobs.push(uploadFilesConcurrent(caseId, files, onUploadProgress));
    } else {
      onUploadProgress?.(emptyProgress(0));
    }

    const batches = await Promise.all(jobs);
    return { uploads: batches.flat() };
  },

  listLlmCalls(versionId: string): Promise<{ calls: LlmCall[] }> {
    return request<{ calls: LlmCall[] }>(
      `/api/versions/${encodeURIComponent(versionId)}/llm-calls`,
    );
  },

  listLlmCallsByStep(
    stepId: string,
  ): Promise<{ calls: LlmCall[]; stepId: string }> {
    return request<{ calls: LlmCall[]; stepId: string }>(
      `/api/pipeline/steps/${encodeURIComponent(stepId)}/llm-calls`,
    );
  },

  getPipelineStepArtifacts(
    stepId: string,
  ): Promise<{
    step: PipelineStep;
    input: unknown;
    output: unknown;
  }> {
    return request<{
      step: PipelineStep;
      input: unknown;
      output: unknown;
    }>(`/api/pipeline/steps/${encodeURIComponent(stepId)}/artifacts`);
  },

  getLlmCall(id: string): Promise<LlmCallDetail> {
    return request<LlmCallDetail>(
      `/api/llm-calls/${encodeURIComponent(id)}`,
    );
  },

  createLlmCall(input: {
    caseVersionId: string;
    prompt?: string;
    systemPrompt?: string;
    model?: string;
    role?: string;
  }): Promise<{ call: LlmCall }> {
    return request<{ call: LlmCall }>("/api/llm-calls", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  rerunLlmCall(
    id: string,
    input?: { systemPrompt?: string },
  ): Promise<{
    call: LlmCall;
    rerunOf: string;
    note: string;
    mode?: "retry" | "fork" | "adhoc";
    parentStepId?: string | null;
  }> {
    return request<{
      call: LlmCall;
      rerunOf: string;
      note: string;
      mode?: "retry" | "fork" | "adhoc";
      parentStepId?: string | null;
    }>(`/api/llm-calls/${encodeURIComponent(id)}/rerun`, {
      method: "POST",
      body: JSON.stringify(input ?? {}),
    });
  },

  listPipelineRuns(
    versionId: string,
  ): Promise<{ runs: PipelineRun[] }> {
    return request<{ runs: PipelineRun[] }>(
      `/api/versions/${encodeURIComponent(versionId)}/pipeline/runs`,
    );
  },

  getPipelineRun(
    runId: string,
  ): Promise<{
    run: PipelineRun;
    steps: PipelineStep[];
    costRollup?: CostRollup;
  }> {
    return request<{
      run: PipelineRun;
      steps: PipelineStep[];
      costRollup?: CostRollup;
    }>(`/api/pipeline/runs/${encodeURIComponent(runId)}`);
  },

  getScriptMeta(versionId: string): Promise<ScriptMeta> {
    return request<ScriptMeta>(
      `/api/versions/${encodeURIComponent(versionId)}/script-meta`,
    );
  },

  saveScriptMeta(
    versionId: string,
    input: { script?: string; meta?: Record<string, unknown> },
  ): Promise<ScriptMeta> {
    return request<ScriptMeta>(
      `/api/versions/${encodeURIComponent(versionId)}/script-meta`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  generateMeta(
    versionId: string,
    input?: { script?: string },
  ): Promise<{ meta: Record<string, unknown>; callId: string }> {
    return request<{ meta: Record<string, unknown>; callId: string }>(
      `/api/versions/${encodeURIComponent(versionId)}/meta/generate`,
      { method: "POST", body: JSON.stringify(input ?? {}) },
    );
  },

  startPipeline(
    versionId: string,
    input?: {
      script?: string;
      meta?: Record<string, unknown>;
    },
  ): Promise<StartPipelineResult> {
    return request<StartPipelineResult>(
      `/api/versions/${encodeURIComponent(versionId)}/pipeline/start`,
      { method: "POST", body: JSON.stringify(input ?? {}) },
    );
  },

  async runExtractTool(input: {
    text?: string;
    files?: File[];
  }): Promise<ExtractToolResult> {
    if (input.files && input.files.length > 0) {
      const form = new FormData();
      if (input.text?.trim()) form.set("text", input.text);
      for (const file of input.files) {
        form.append("files", file);
      }
      const res = await fetch("/api/tools/extract", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        throw await parseError(res);
      }
      return (await res.json()) as ExtractToolResult;
    }
    return request<ExtractToolResult>("/api/tools/extract", {
      method: "POST",
      body: JSON.stringify({ text: input.text ?? "" }),
    });
  },

  runScriptCheck(input: { script: string }): Promise<ScriptCheckResult> {
    return request<ScriptCheckResult>("/api/tools/script-check", {
      method: "POST",
      body: JSON.stringify({ script: input.script }),
    });
  },

  listJsonEdits(
    caseId: string,
    query?: { locale?: string; versionId?: string; limit?: number },
  ): Promise<{ logs: PhoneJsonEditLog[] }> {
    const params = new URLSearchParams();
    if (query?.locale) params.set("locale", query.locale);
    if (query?.versionId) params.set("versionId", query.versionId);
    if (query?.limit != null) params.set("limit", String(query.limit));
    const qs = params.toString();
    return request<{ logs: PhoneJsonEditLog[] }>(
      `/api/cases/${encodeURIComponent(caseId)}/json-edits${qs ? `?${qs}` : ""}`,
    );
  },

  getPhoneJson(versionId: string): Promise<PhoneJsonResult> {
    return request<PhoneJsonResult>(
      `/api/versions/${encodeURIComponent(versionId)}/phone-json`,
    );
  },

  savePhoneJson(
    versionId: string,
    input: {
      phoneJson?: unknown;
      text?: string;
      asNewVersion?: boolean;
      branchLabel?: string;
    },
  ): Promise<SavePhoneJsonResult> {
    return request<SavePhoneJsonResult>(
      `/api/versions/${encodeURIComponent(versionId)}/phone-json`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },

  autoFixPhoneJson(
    versionId: string,
    input: { phoneJson?: unknown; text?: string },
  ): Promise<AutoFixPhoneJsonResult> {
    return request<AutoFixPhoneJsonResult>(
      `/api/versions/${encodeURIComponent(versionId)}/phone-json/auto-fix`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  forkVersion(
    versionId: string,
    input?: {
      branchLabel?: string;
      copyUploads?: boolean;
      copyAssets?: boolean;
    },
  ): Promise<ForkVersionResult> {
    return request<ForkVersionResult>(
      `/api/versions/${encodeURIComponent(versionId)}/fork`,
      { method: "POST", body: JSON.stringify(input ?? {}) },
    );
  },

  previewCreateVersionPrompts(
    caseId: string,
    input: {
      startFrom: StartFromStep;
      sourceVersionId?: string;
      locale?: string;
    },
  ): Promise<CreateVersionPromptPreview> {
    return request<CreateVersionPromptPreview>(
      `/api/cases/${encodeURIComponent(caseId)}/versions/prompt-preview`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  createVersion(
    caseId: string,
    input: {
      startFrom: StartFromStep;
      sourceVersionId?: string;
      locale?: string;
      branchLabel?: string;
      systemPrompt?: string;
      userPrompt?: string;
    },
  ): Promise<CreateVersionResult> {
    return request<CreateVersionResult>(
      `/api/cases/${encodeURIComponent(caseId)}/versions`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  deleteVersion(versionId: string): Promise<DeleteVersionResult> {
    return request<DeleteVersionResult>(
      `/api/versions/${encodeURIComponent(versionId)}`,
      { method: "DELETE" },
    );
  },

  listAssets(versionId: string): Promise<{ assets: CaseAsset[] }> {
    return request<{ assets: CaseAsset[] }>(
      `/api/versions/${encodeURIComponent(versionId)}/assets`,
    );
  },

  listPeopleVoice(
    versionId: string,
  ): Promise<{ peopleVoice: PeopleVoice[] }> {
    return request<{ peopleVoice: PeopleVoice[] }>(
      `/api/versions/${encodeURIComponent(versionId)}/people-voice`,
    );
  },

  getMediaReadiness(
    versionId: string,
    phoneJson?: unknown,
  ): Promise<MediaReadinessResult> {
    if (phoneJson !== undefined) {
      return request<MediaReadinessResult>(
        `/api/versions/${encodeURIComponent(versionId)}/media-readiness`,
        { method: "POST", body: JSON.stringify({ phoneJson }) },
      );
    }
    return request<MediaReadinessResult>(
      `/api/versions/${encodeURIComponent(versionId)}/media-readiness`,
    );
  },

  getCasePreview(
    caseId: string,
    opts?: { versionId?: string },
  ): Promise<CasePreview> {
    const params = new URLSearchParams();
    if (opts?.versionId) params.set("versionId", opts.versionId);
    const qs = params.toString();
    return request<CasePreview>(
      `/api/cases/${encodeURIComponent(caseId)}/preview${qs ? `?${qs}` : ""}`,
    );
  },

  getPublishGate(versionId: string): Promise<PublishGateResult> {
    return request<PublishGateResult>(
      `/api/versions/${encodeURIComponent(versionId)}/publish-gate`,
    );
  },

  /**
   * Translate status for a source version (placeholder readiness + per-locale runs).
   */
  getTranslateStatus(versionId: string): Promise<TranslateStatus> {
    return request<TranslateStatus>(
      `/api/versions/${encodeURIComponent(versionId)}/translate`,
    );
  },

  /**
   * Start translate pipeline(s). Omit locales to start the default set
   * (es, tr, de, fr, pt, pt-BR, zh-HK, zh-MO, zh-TW, ja-JP, ko-KR).
   */
  startTranslate(
    versionId: string,
    input?: {
      targetLocale?: string;
      locales?: string[];
      forceNew?: boolean;
    },
  ): Promise<StartTranslateBatchResult> {
    return request<StartTranslateBatchResult>(
      `/api/versions/${encodeURIComponent(versionId)}/translate`,
      {
        method: "POST",
        body: JSON.stringify(input ?? {}),
      },
    );
  },

  /** @deprecated use startTranslate */
  translateVersion(
    versionId: string,
    targetLocale: string,
  ): Promise<StartTranslateBatchResult> {
    return this.startTranslate(versionId, { targetLocale });
  },

  getCasePublish(caseId: string): Promise<CasePublishStatus> {
    return request<CasePublishStatus>(
      `/api/cases/${encodeURIComponent(caseId)}/publish`,
    );
  },

  /**
   * Re-pin D1 phone_runtime_version for a version.
   * Concrete pins assert R2 release tree; latest skips tree check at write.
   */
  updateVersionPhoneRuntime(
    versionId: string,
    phoneRuntimeVersion: string,
  ): Promise<UpdatePhoneRuntimeResult> {
    return request<UpdatePhoneRuntimeResult>(
      `/api/versions/${encodeURIComponent(versionId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ phoneRuntimeVersion }),
      },
    );
  },

  publishCase(
    caseId: string,
    input?: {
      locales?: string[];
      versionIds?: string[];
      force?: boolean;
      /** When D1 pin is latest, stamp published body as literal latest (default false). */
      publishAsLatest?: boolean;
      /** Selected user group ids. Empty/omit = full release (uids: []). */
      groupIds?: string[];
    },
  ): Promise<PublishCaseResult> {
    return request<PublishCaseResult>(
      `/api/cases/${encodeURIComponent(caseId)}/publish`,
      {
        method: "POST",
        body: JSON.stringify(input ?? {}),
      },
    );
  },

  /**
   * Unpublish one or more production locales (OPAPI delete with locale).
   * Pass a single versionId or multiple versionIds.
   */
  unpublishCase(
    caseId: string,
    input: string | { versionId?: string; versionIds?: string[] },
  ): Promise<UnpublishCaseResult> {
    const body =
      typeof input === "string"
        ? { versionId: input }
        : {
            versionId: input.versionId,
            versionIds: input.versionIds,
          };
    return request<UnpublishCaseResult>(
      `/api/cases/${encodeURIComponent(caseId)}/unpublish`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * Bulk promote published cases to a concrete phone runtime.
   * Same validatorId only; R2 preflight; OPAPI on apply (when dryRun is false).
   */
  bulkPromotePhoneRuntime(input: {
    toVersion: string;
    fromVersions?: string[];
    scope?: "published";
    dryRun?: boolean;
  }): Promise<BulkPromotePhoneRuntimeResult> {
    return request<BulkPromotePhoneRuntimeResult>(
      "/api/phone-runtime/bulk-promote",
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },

  continuePipelineRun(
    runId: string,
  ): Promise<{
    run: PipelineRun;
    steps: PipelineStep[];
    stale?: boolean;
    kicked?: "ok" | "timeout";
    note?: string;
  }> {
    return request<{
      run: PipelineRun;
      steps: PipelineStep[];
      stale?: boolean;
      kicked?: "ok" | "timeout";
      note?: string;
    }>(
      `/api/pipeline/runs/${encodeURIComponent(runId)}/continue`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  replaceAsset(
    versionId: string,
    assetId: string,
    file: File,
  ): Promise<{ asset: CaseAsset | null }> {
    const form = new FormData();
    form.set("file", file);
    return request<{ asset: CaseAsset | null }>(
      `/api/versions/${encodeURIComponent(versionId)}/assets/${encodeURIComponent(assetId)}/replace`,
      { method: "POST", body: form },
    );
  },

  /**
   * Upload a new media asset for phone JSON form media_url fields.
   * Returns publicUrl for the client to write into the draft.
   */
  uploadAsset(
    versionId: string,
    file: File,
    opts?: { fieldKey?: string },
  ): Promise<{ asset: CaseAsset | null }> {
    const form = new FormData();
    form.set("file", file);
    if (opts?.fieldKey) form.set("fieldKey", opts.fieldKey);
    return request<{ asset: CaseAsset | null }>(
      `/api/versions/${encodeURIComponent(versionId)}/assets/upload`,
      { method: "POST", body: form },
    );
  },
};

export interface PipelineRun {
  id: string;
  caseVersionId: string;
  stage: string;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PipelineStep {
  id: string;
  runId: string;
  stepKey: string;
  status: string;
  model: string | null;
  systemPrompt: string | null;
  userPrompt: string | null;
  inputR2Key: string | null;
  outputR2Key: string | null;
  streamR2Key: string | null;
  usage: Record<string, unknown> | null;
  costUsd: number | null;
  durationMs: number | null;
  parentStepId: string | null;
  attemptN: number;
  errorText: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CreateVersionPromptPreviewMode =
  | "single_text"
  | "agent_seed"
  | "multi_media";

export interface CreateVersionPromptPreview {
  startFrom: StartFromStep;
  mode: CreateVersionPromptPreviewMode;
  systemPrompt: string;
  userPrompt: string;
  note: string | null;
  meta?: { unitCount?: number; unitLabel?: string };
}

export interface ScriptMeta {
  versionId: string;
  caseId?: string;
  status: string;
  script: string | null;
  meta: Record<string, unknown> | null;
  cleanedScriptR2Key?: string | null;
}

export interface StartPipelineResult {
  version: {
    id: string;
    status: string;
    meta: Record<string, unknown> | null;
  };
  run?: PipelineRun;
  stageBRun: PipelineRun;
  steps?: PipelineStep[];
  note: string;
  caseId: string;
}

export interface ExtractToolResult {
  text: string;
  filename: string;
  model: string;
  items: Array<{
    ok: boolean;
    kind: string;
    filename: string | null;
    chars?: number;
    error?: string;
  }>;
}

export type ScriptSupport = "supported" | "partial" | "unsupported";

export interface ScriptRequirementFinding {
  requirement: string;
  schemaPath: string | null;
  support: ScriptSupport;
  severity: "ok" | "warn" | "error";
  evidence: string;
  note: string;
  rewriteHint: string | null;
}

export interface ScriptCheckIssue {
  severity: "error" | "warn" | "info";
  code: string;
  title: string;
  detail: string;
  suggestion: string | null;
  schemaPath: string | null;
}

export interface ScriptCheckResult {
  ok: boolean;
  summary: string;
  findings: ScriptRequirementFinding[];
  issues: ScriptCheckIssue[];
  caseSchema: string;
  model: string;
}

/** Pipeline review step output (ops-only; does not mutate phone JSON). */
export interface ReviewFinding {
  title: string;
  severity: "ok" | "warn" | "error";
  location: string | null;
  evidence: string;
  note: string;
  suggestion: string | null;
}

export interface ReviewIssue {
  severity: "error" | "warn" | "info";
  code: string;
  title: string;
  detail: string;
  suggestion: string | null;
  location: string | null;
}

export interface ReviewImageDescription {
  path: string;
  url: string;
  description: string;
  ok: boolean;
}

export interface ReviewResult {
  ok: boolean;
  summary: string;
  findings: ReviewFinding[];
  issues: ReviewIssue[];
  images: ReviewImageDescription[];
  imageCount: number;
  imageFailures: number;
  model: string;
  notes: string;
}

export type JsonEditKind = "changed" | "added" | "removed" | "moved";

export interface JsonEditChange {
  path: string;
  label: string;
  section: string;
  kind: JsonEditKind;
  from: string | null;
  to: string | null;
  fromIndex?: number;
  toIndex?: number;
}

export interface JsonEditPayload {
  changes: JsonEditChange[];
  omitted: number;
  sections: string[];
}

export interface PhoneJsonEditLog {
  id: string;
  caseId: string;
  caseVersionId: string;
  locale: string;
  versionN: number;
  actorEmail: string;
  occurredAt: string;
  summary: string;
  changeCount: number;
  payload: JsonEditPayload | null;
}

export interface PhoneJsonResult {
  versionId: string;
  phoneJsonR2Key: string | null;
  phoneJson: unknown | null;
  placeholderCount: number;
}

export interface SavePhoneJsonResult extends PhoneJsonResult {
  forkedFrom: string | null;
  note: string;
  /** Set when saving a published version (stable CDN URL for OPAPI). */
  publishedUrl?: string | null;
  /** Sanitized OPAPI result when a published version was re-imported. */
  opapi?: Record<string, unknown> | null;
}

export interface AutoFixPhoneJsonResult {
  versionId: string;
  phoneJson: unknown;
  issues: Array<{ path: string; message: string }>;
  ok: boolean;
  changed: boolean;
  reason:
    | "already_ok"
    | "validate_ok"
    | "validate_quota"
    | "iteration_cap"
    | "llm_failed";
  validateCalls: number;
  iterations: number;
  callIds: string[];
  totalCostUsd: number;
  error: string | null;
  saved: false;
}

export interface ForkVersionResult {
  source: { id: string; versionN: number; status: string };
  version: CaseVersion;
  copied: {
    phoneJson: boolean;
    script: boolean;
    uploads: number;
    assets: number;
    peopleVoice: number;
  };
  note: string;
  mode: string;
}

export interface CreateVersionResult extends ForkVersionResult {
  startFrom: StartFromStep;
  stage: "pipeline" | "A" | "B";
  run: PipelineRun;
  steps: PipelineStep[];
}

export interface DeleteVersionResult {
  ok: true;
  versionId: string;
  caseId: string;
  locale: string;
  versionN: number;
  r2Deleted: number;
}

export interface DeleteCaseResult {
  ok: true;
  caseId: string;
  deletedVersionIds: string[];
  r2Deleted: number;
}

export interface CostRollup {
  totalUsd: number;
  stepCostUsd: number;
  llmCallCostUsd: number;
  stepCount: number;
  succeededSteps: number;
  failedSteps: number;
  runningSteps: number;
  llmCallCount: number;
  byStepKey: Array<{ stepKey: string; costUsd: number; status: string }>;
}

export interface CaseAsset {
  id: string;
  caseVersionId: string;
  placeholderTag: string;
  type: string;
  description: string;
  characterIds: string[];
  r2Key: string | null;
  publicUrl: string | null;
  voiceProfile: Record<string, unknown> | null;
  genMeta: Record<string, unknown> | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface PeopleVoice {
  caseVersionId: string;
  characterId: string;
  voiceId: string | null;
  headRefR2Key: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface MediaReadinessResult {
  versionId: string;
  ok: boolean;
  total: number;
  placeholderCount: number;
  issues: Array<{ path: string; url: string; reason: string }>;
  assetsReady: number;
  assetsTotal: number;
}

export interface CasePreviewLocale {
  locale: string;
  versionId: string;
  versionN: number;
  status: string;
  phoneRuntimeVersion: string;
  caseJsonUrl: string;
  iframeSrc: string;
  /** Simplified Chinese walkthrough for ops QA (post phone JSON). */
  experienceGuideMarkdown: string | null;
  experienceGuidePresent: boolean;
}

export interface CasePreview {
  caseId: string;
  phonePreviewBase: string;
  /** Phone shell UI locales (independent of case JSON language). */
  phoneUiLocales: string[];
  locales: CasePreviewLocale[];
}

export interface PublishGateIssue {
  path: string;
  url?: string;
  reason: string;
}

export interface PublishGateResult {
  ok: boolean;
  versionId: string;
  status: string;
  validateOk: boolean;
  placeholderCount: number;
  mediaOk: boolean;
  mediaTotal: number;
  /** Concrete phone version used for validate. */
  phoneVersion: string;
  /** Raw D1 pin (may be latest). */
  pin?: string;
  resolveSource?: string;
  phoneReleasePresent?: boolean;
  validatorId?: string;
  issues: PublishGateIssue[];
}

export interface TranslateResult {
  sourceVersion: CaseVersion;
  version: CaseVersion;
  targetLocale: string;
  callId: string;
  note: string;
}

export interface TranslateLocaleStatus {
  locale: string;
  label: string | null;
  version: CaseVersion | null;
  run: PipelineRun | null;
  steps: PipelineStep[];
}

export interface TranslateStatus {
  sourceVersion: CaseVersion;
  placeholdersReady: boolean;
  placeholderSource: "snapshot" | "live" | "reconstructed" | null;
  targetLocales: Array<{ locale: string; label: string }>;
  locales: TranslateLocaleStatus[];
}

export interface StartTranslateItemOk {
  ok: true;
  targetLocale: string;
  created: boolean;
  version: CaseVersion;
  run: PipelineRun;
  steps: PipelineStep[];
}

export interface StartTranslateItemErr {
  ok: false;
  error: string;
  detail: string | null;
  targetLocale: string | null;
}

export interface StartTranslateBatchResult {
  sourceVersion: CaseVersion;
  started: number;
  failed: number;
  results: Array<StartTranslateItemOk | StartTranslateItemErr>;
  note: string;
}

export interface CasePublishLocale {
  locale: string;
  publishedVersionId: string | null;
  publishedUrl: string;
  packageCount: number;
  latestVersionId: string | null;
  latestVersionN: number | null;
  latestStatus: string | null;
  /** D1 pin for published version (or latest draft with phone). */
  d1PhoneRuntimeVersion: string | null;
  /** Stamped phone_runtime_version on published CDN JSON, when present. */
  publishedPhoneRuntimeVersion: string | null;
  /** User-group ids from the last publish snapshot. Empty = full release. */
  groupIds: string[];
  /** Group names stored at publish time (may be empty on older snapshots). */
  publishGroups: Array<{ id: string; name: string }>;
  lastOpapi: unknown;
}

export interface CasePublishStatus {
  caseId: string;
  timezone: string;
  sortOrder: number;
  locales: CasePublishLocale[];
  opapiConfigured: boolean;
}

export interface UpdatePhoneRuntimeResult {
  version: CaseVersion;
  from: string;
  to: string;
}

export interface PublishCaseResult {
  caseId: string;
  ok: boolean;
  urls: string[];
  gateResults: PublishGateResult[];
  published: Array<{
    locale: string;
    versionId: string;
    versionN: number;
    status: string;
    publishedUrl: string;
    previousPublishedVersionId: string | null;
    phoneRuntimeVersion?: string;
    d1Pin?: string;
    stampMode?: string;
  }>;
  /** Per-locale sanitized OPAPI results, keyed by locale. */
  opapi: Record<string, unknown>;
  /** Selected group ids used for this publish (empty = full release). */
  groupIds?: string[];
  /** Merged player UIDs sent to OPAPI (empty = full release). */
  uids?: number[];
}

/** Human-readable OPAPI failure from a single result or per-locale map. */
export function formatOpapiFailure(opapi: Record<string, unknown>): string {
  if (typeof opapi.error === "string" && opapi.error) {
    return opapi.error;
  }
  const parts: string[] = [];
  for (const [key, value] of Object.entries(opapi)) {
    if (!value || typeof value !== "object") continue;
    const entry = value as Record<string, unknown>;
    if (entry.ok === false) {
      parts.push(`${key}:${String(entry.error ?? "failed")}`);
    }
  }
  return parts.length > 0 ? parts.join(", ") : "opapi_error";
}

/**
 * Toast / alert body for publish API errors.
 * Gate notes are already multi-line; do not glue the error code onto the same line.
 */
export function formatPublishFailureDetail(
  code: string,
  note?: string,
): string {
  if (!note || note === code) return code;
  if (code === "publish_gate_failed") return note;
  return `${code}: ${note}`;
}

export interface UnpublishCaseItem {
  versionId: string;
  locale: string;
  version: CaseVersion;
  opapi: Record<string, unknown>;
}

export interface UnpublishCaseResult {
  caseId: string;
  ok: boolean;
  opapiMode: "delete";
  storyId: number | null;
  unpublished: UnpublishCaseItem[];
  /** First target (compat with single-locale clients). */
  versionId: string;
  locale: string;
  version: CaseVersion;
  opapi: Record<string, unknown>;
}

export interface BulkPromotePhoneRuntimeItem {
  caseId: string;
  locale: string;
  versionId: string;
  fromVersion: string;
  toVersion: string;
  action: "skip" | "would_update" | "updated" | "failed";
  reason?: string;
  publicUrl?: string;
}

export interface BulkPromotePhoneRuntimeResult {
  ok: boolean;
  dryRun: boolean;
  toVersion: string;
  fromVersions: string[];
  validatorId: string;
  scope: "published";
  matched: number;
  skipped: number;
  updated: number;
  failed: number;
  opapiCases: number;
  sampleCaseIds: string[];
  items: BulkPromotePhoneRuntimeItem[];
}

export interface LlmCall {
  id: string;
  stepId: string | null;
  assetId: string | null;
  agentSessionId: string | null;
  caseVersionId: string | null;
  model: string;
  productName: string | null;
  systemPrompt: string | null;
  inputR2Key: string | null;
  outputR2Key: string | null;
  streamR2Key: string | null;
  usage: Record<string, unknown> | null;
  costUsd: number | null;
  durationMs: number | null;
  status: string;
  errorText: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface LlmCallDetail {
  call: LlmCall;
  input: unknown;
  output: unknown;
  streamTranscript: string | null;
}
